import { useQuery } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

// Bumped to v2 after switching from backend endpoint (returned { nodes: [...] })
// to bundled JSON (returns a raw array). Prevents stale cache from serving the
// old shape to the new select() function.
export const wzCodeKeys = { tree: ['wzCodes', 'tree', 'v2'] };

/**
 * Dynamically imports the bundled WZ-2025 tree JSON so Webpack code-splits it
 * into its own chunk. The chunk is loaded lazily on first use (modal open)
 * and browser-cached afterwards.
 */
const loadWzTree = async () => {
	const mod = await import(
		/* webpackChunkName: "wz2025-codes" */
		'../../data/wz2025-codes.json'
	);
	// Unwrap potential ESM default wrapping (Webpack may deliver either shape)
	let data = mod;
	while (data && !Array.isArray(data) && data.default) {
		data = data.default;
	}
	return Array.isArray(data) ? data : [];
};

/**
 * Loads the bundled WZ-2025 hierarchy and pre-builds lookup structures
 * for fast tree rendering and autocomplete. Data is static, so cache forever.
 */
export const useWzCodesTree = () =>
	useQuery({
		queryKey: wzCodeKeys.tree,
		queryFn: loadWzTree,
		staleTime: STALE_TIMES.STATIC,
		gcTime: Infinity,
		select: (nodes) => {
			const list = Array.isArray(nodes) ? nodes : [];
			const byCode = new Map();
			const childrenByParent = new Map();

			for (const n of list) byCode.set(n.code, n);
			for (const n of list) {
				if (!n.parentCode) continue;
				if (!childrenByParent.has(n.parentCode)) childrenByParent.set(n.parentCode, []);
				childrenByParent.get(n.parentCode).push(n);
			}

			const roots = list.filter((n) => n.level === 1);
			return { nodes: list, byCode, childrenByParent, roots };
		},
	});
